/* eslint-disable import/no-cycle */
import { createSelector } from 'reselect';

// eslint-disable-next-line import/no-cycle
import {
  addKRAtPaths,
  updateObjectiveUsingPaths,
  updateObjectiveUsingPathsToggleKr,
  getUpdatedAlignmentTree,
  replaceObjectiveInList,
  createPathMap,
  collapseObjectives,
  toggleObjectiveOpenStateAtPath,
  replaceObjectiveAlignment,
  deleteObjectiveUsingPaths,
  deleteDependencyObjectivesUsingPaths,
  updateMatchingObjectiveFromListOfObjectives,
  updateObjectiveDetailsRealtimeUpdate,
  reorderObjectives,
  reorderObjectivesUnderSameParent,
  reorderObjectivesUnderDiffParent,
  flatDeep,
  updateViewInContext,
  clearAlignedObjective
} from 'utils/objectives.utils';
import { updateObjectiveVisibilityUsingPaths } from 'utils/visibility.utils';
import dndUndoUtils from 'utils/dndUndo.utils';
import { actions as appActions } from 'containers/App/store/actions';
import { actions as DnDActions } from 'containers/Home/store/actions';

import { actions } from './actions';

const initialState = {
  pathMap: null,
  objectives: null,
  paginationData: {
    currentPage: 1,
    showPagination: false
  },
  orderingVersion: null,
  selectedObjective: null,
  selectedObjectiveToMove: null,
  selectedObjectiveToLink: null,
  linkedObjectives: null,
  tempStateObjectives: null,
  viewDashboardData: null,
  objectiveUpdationDetails: {
    objective_id: null,
    timestamp: null
  },
  flatObjectives: [],
  linkedObjectivePopup: null,
  mobileTreeTraverseData: null,
  showObjectiveLoader: null,
  selectedTeamData: null,
  updateViewDashboardData: false,
  recentlyDroppedObjective: null /* Objective details of the last dropped objective using dnd */,
  quickFilterAttributes: [],
  quickFiltersSelected: null,
  selectedAnnualGoalForAlignment: null,
  createObjectiveSelectedCycle: null,
  createObjectiveActionStatus: null,
  annuallyAlignedObjectiveDetails: null,
  annualLinkedObjPopupData: null,
  selectedTeamQuickFilters: null,
  pageLoadInProgress: false,
  lastObjectiveInViewId: null,
  viewAlignmentPreviousViewState: null,
  currentUserRestrictions: {}
};

// Memorized  Selector Function
export const getObjectivesSelector = createSelector(
  state => state.objectivesReducer.objectives,
  objectives => objectives
);

export const getOrderingVersionSelector = createSelector(
  state => state.objectivesReducer.orderingVersion,
  orderingVersion => orderingVersion
);

export const getFlatObjectivesSelector = createSelector(
  state => state.objectivesReducer.flatObjectives,
  flatObjectives => flatObjectives
);

export const getSelectedObjectiveSelector = createSelector(
  state => state.objectivesReducer.selectedObjective,
  selectedObjective => selectedObjective
);

export const getSelectedObjectiveToMoveSelector = createSelector(
  state => state.objectivesReducer.selectedObjectiveToMove,
  selectedObjectiveToMove => selectedObjectiveToMove
);

export const getPathMapSelector = createSelector(
  state => state.objectivesReducer.pathMap,
  pathMap => pathMap
);

export const getSelectedObjectiveToLinkSelector = createSelector(
  state => state.objectivesReducer.selectedObjectiveToLink,
  selectedObjectiveToLink => selectedObjectiveToLink
);

export const getLinkedObjectivesSelector = createSelector(
  state => state.objectivesReducer.linkedObjectives,
  linkedObjectives => linkedObjectives
);

export const getViewDashboardData = createSelector(
  state => state.objectivesReducer.viewDashboardData,
  viewDashboardData => viewDashboardData
);

export const getRecentlyUpdatedObjectiveDetails = createSelector(
  state => state.objectivesReducer.objectiveUpdationDetails,
  objectiveUpdationDetails => objectiveUpdationDetails
);

export const getLinkedObjectivePopupSelector = createSelector(
  state => state.objectivesReducer.linkedObjectivePopup,
  linkedObjectivePopup => linkedObjectivePopup
);

export const getAlignedObjectiveSelector = createSelector(
  state => state.objectivesReducer.alignedObjective,
  alignedObjective => alignedObjective
);

export const getMobileTraversedTreeData = createSelector(
  state => state.objectivesReducer.mobileTreeTraverseData,
  mobileTreeTraverseData => mobileTreeTraverseData
);

export const getSelectedTeamDataSelector = createSelector(
  state => state.objectivesReducer.selectedTeamData,
  selectedTeamData => selectedTeamData
);

export const getRecentlyDroppedObjectiveSelector = createSelector(
  state => state.objectivesReducer.recentlyDroppedObjective,
  recentlyDroppedObjective => recentlyDroppedObjective
);

export const getSelectedAnnualGoalForAlignment = createSelector(
  state => state.objectivesReducer.selectedAnnualGoalForAlignment,
  selectedAnnualGoalForAlignment => selectedAnnualGoalForAlignment
);

export const getCreateObjectiveSelectedCycle = createSelector(
  state => state.objectivesReducer.createObjectiveSelectedCycle,
  createObjectiveSelectedCycle => createObjectiveSelectedCycle
);

export const getCreateObjectiveActionStatus = createSelector(
  state => state.objectivesReducer.createObjectiveActionStatus,
  createObjectiveActionStatus => createObjectiveActionStatus
);

export const getAnnuallyAlignedObjectiveDetails = createSelector(
  state => state.objectivesReducer.annuallyAlignedObjectiveDetails,
  annuallyAlignedObjectiveDetails => annuallyAlignedObjectiveDetails
);

export const getAnnualLinkedObjPopupData = createSelector(
  state => state.objectivesReducer.annualLinkedObjPopupData,
  annualLinkedObjPopupData => annualLinkedObjPopupData
);

export const getPaginationData = createSelector(
  state => state.objectivesReducer.paginationData,
  paginationData => paginationData
);

export const getSelectedQuickFilters = createSelector(
  state => state.objectivesReducer.quickFiltersSelected,
  quickFiltersSelected => quickFiltersSelected
);

export const getSelectedTeamQuickFilters = createSelector(
  state => state.objectivesReducer.selectedTeamQuickFilters,
  selectedTeamQuickFilters => selectedTeamQuickFilters
);

export const getIsPageLoadInProgress = createSelector(
  state => state.objectivesReducer.pageLoadInProgress,
  pageLoadInProgress => pageLoadInProgress
);

export const getLastObjectiveInViewId = createSelector(
  state => state.objectivesReducer.lastObjectiveInViewId,
  lastObjectiveInViewId => lastObjectiveInViewId
);

export const getShowObjectiveLoader = createSelector(
  state => state.objectivesReducer.showObjectiveLoader,
  showObjectiveLoader => showObjectiveLoader
);

export const getViewAlignmentPreviousViewState = createSelector(
  state => state.objectivesReducer.viewAlignmentPreviousViewState,
  viewAlignmentPreviousViewState => viewAlignmentPreviousViewState
);

export const getCurrentUserRestrictions = createSelector(
  state => state.objectivesReducer.currentUserRestrictions,
  currentUserRestrictions => currentUserRestrictions
);

export default (state = initialState, payload = {}) => {
  switch (payload.type) {
    case actions.GOTO_NEXT_PAGE: {
      return {
        ...state,
        paginationData: {
          ...state.paginationData,
          currentPage: state.paginationData.currentPage + 1,
          showPagination:
            state.paginationData.currentPage <
            state.paginationData?.metadata?.total_pages
        }
      };
    }

    case actions.RESET_PAGINATION_DATA: {
      return {
        ...state,
        paginationData: {
          currentPage: 1,
          showPagination: false
        }
      };
    }

    case actions.SAVE_OBJECTIVES: {
      let pathMap = null;
      const objectives = payload.data
        ? (payload.appendObjectives
            ? [...(state.objectives || [])].concat(payload.data || [])
            : payload.data
          ).filter(
            ({ visible, sub_tree_accessible }) => visible || sub_tree_accessible
          )
        : null;
      if (objectives) {
        pathMap = createPathMap(objectives);
      }
      const flatObjectives = flatDeep(objectives, Infinity);
      return {
        ...state,
        pathMap,
        objectives,
        flatObjectives,
        orderingVersion: payload.orderingVersion,
        paginationData: {
          ...state.paginationData,
          ...payload.paginationData,
          showPagination:
            state.paginationData.currentPage <
            payload.paginationData?.metadata?.total_pages
        }
      };
    }

    case actions.SHOW_OBJECTIVE_LOADER: {
      return {
        ...state,
        showObjectiveLoader: payload.value || false
      };
    }

    case actions.SET_SELECTED_OBJECTIVE:
      return {
        ...state,
        selectedObjective: {
          ...payload.data,
          parent_objective_owner: payload.parent_objective_owner
        }
      };

    case actions.CLEAR_SELECTED_OBJECTIVE:
      return {
        ...state,
        selectedObjective: null
      };

    case actions.CLEAR_SELECTED_OBJECTIVE_FOCUS:
      return {
        ...state
      };

    case actions.SAVE_NEW_KR: {
      const paths = state.pathMap[payload.parent_objective_id];
      const updatedObjectives = addKRAtPaths(
        state.objectives,
        payload.objective,
        paths
      );
      const newPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        pathMap: newPathMap,
        objectives: updatedObjectives,
        flatObjectives
      };
    }

    case actions.SAVE_NEW_DEPENDENCY_OBJECTIVE: {
      const paths = state.pathMap[payload.parent_objective_id];
      const updatedObjectives = addKRAtPaths(
        state.objectives,
        payload.objective,
        paths
      );
      const newPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        pathMap: newPathMap,
        objectives: updatedObjectives,
        flatObjectives
      };
    }

    case actions.SAVE_OBJECTIVE_ALIGNMENT: {
      const index = state.objectives.findIndex(
        item => item.objective_id === payload.objective.objective_id
      );
      const updatedObjective = getUpdatedAlignmentTree(
        payload.data,
        payload.objective
      );
      const updatedObjectives = replaceObjectiveInList(
        state.objectives,
        updatedObjective,
        index
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        pathMap: updatedPathMap,
        objectives: updatedObjectives,
        flatObjectives
      };
    }

    case actions.UPDATE_OBJECTIVE: {
      const paths =
        payload.objective?.objective_id && state.pathMap
          ? state.pathMap[payload.objective?.objective_id]
          : null;
      const updatedObjectives = updateObjectiveUsingPaths(
        state.objectives,
        payload.objective,
        paths,
        payload.updateKRs
      );
      let updatedPathMap = state.pathMap;
      if (payload.updateKRs) {
        updatedPathMap = createPathMap(updatedObjectives);
      }
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.UPDATE_OBJECTIVES: {
      let paths = null;
      let updatedObjectives = null;
      payload.objectives?.forEach(obj => {
        paths =
          obj?.objective_id && state.pathMap
            ? state.pathMap[obj?.objective_id]
            : null;
        updatedObjectives = updateObjectiveUsingPaths(
          state.objectives,
          obj,
          paths,
          payload.updateKRs
        );
      });
      let updatedPathMap = state.pathMap;
      if (payload.updateKRs) {
        updatedPathMap = createPathMap(updatedObjectives);
      }
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.UPDATE_OBJECTIVES_VISIBILITY: {
      const paths =
        payload.objective?.objective_id && state.pathMap
          ? state.pathMap[payload.objective?.objective_id]
          : null;
      const updatedObjectives = updateObjectiveVisibilityUsingPaths(
        state.objectives,
        payload.objective,
        paths
      );
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives
      };
    }

    case actions.UPDATE_OBJECTIVE_TOGGLE_KRS: {
      const paths =
        payload.objective?.objective_id && state.pathMap
          ? state.pathMap[payload.objective?.objective_id]
          : null;
      const updatedObjectives = updateObjectiveUsingPathsToggleKr(
        state.objectives,
        payload.objective,
        paths
      );
      let updatedPathMap = state.pathMap;
      updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.UPDATE_OBJECTIVE_REALTIME_EVENT: {
      const { objective, updateView, isSearchActive } = payload;
      const { objective_id: objectiveId } = objective;
      let {
        selectedObjective,
        linkedObjectives,
        tempStateObjectives,
        selectedObjectiveToMove,
        selectedObjectiveToLink
      } = state;
      let updatedObjectives = state.objectives;
      let updatedPathMap = state.pathMap;
      if (updateView && updatedPathMap) {
        const paths = updatedPathMap[objectiveId];
        updatedObjectives = updateObjectiveUsingPaths(
          state.objectives,
          objective,
          paths,
          payload.updateKRs
        );
        if (payload.updateKRs) {
          updatedPathMap = createPathMap(updatedObjectives);
        }
      }
      if (objectiveId === state.selectedObjective?.objective_id) {
        const parent_objective_owner = objective.parent_objective_owner
          ? objective.parent_objective_owner
          : selectedObjective.parent_objective_owner;
        selectedObjective = {
          ...updateObjectiveDetailsRealtimeUpdate(selectedObjective, objective),
          parent_objective_owner
        };
      }
      linkedObjectives = updateMatchingObjectiveFromListOfObjectives(
        linkedObjectives,
        objective
      );
      if (!isSearchActive)
        tempStateObjectives = updateMatchingObjectiveFromListOfObjectives(
          tempStateObjectives,
          objective
        );
      if (objectiveId === selectedObjectiveToMove?.objective_id) {
        selectedObjectiveToMove = updateObjectiveDetailsRealtimeUpdate(
          selectedObjectiveToMove,
          objective
        );
      }
      if (objectiveId === selectedObjectiveToLink?.objective_id) {
        selectedObjectiveToLink = updateObjectiveDetailsRealtimeUpdate(
          selectedObjectiveToLink,
          objective
        );
      }
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        selectedObjective,
        linkedObjectives,
        selectedObjectiveToMove,
        selectedObjectiveToLink,
        tempStateObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.TOGGLE_OBJECTIVE_OPEN_STATE: {
      const { rootObjectiveIndex } = payload.objective;
      const updatedObjectives = toggleObjectiveOpenStateAtPath(
        state.objectives,
        payload.objective,
        state.pathMap[payload?.objective?.objective_id][rootObjectiveIndex]
      );
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives
      };
    }

    case actions.COLLAPSE_OBJECTIVES: {
      const updatedObjectives = collapseObjectives(state.objectives);
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);

      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.HIDE_OBJECTIVE_ALIGNMENT: {
      const updatedObjectives = replaceObjectiveAlignment(
        state.objectives,
        payload.objective
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.SET_MOVE_OBJECTIVE: {
      return {
        ...state,
        selectedObjectiveToMove: payload.objective
      };
    }

    case actions.UPDATE_OBJECTIVE_DETAILS_ACTION: {
      return {
        ...state,
        objectiveUpdationDetails: {
          objective_id: payload.objective_id,
          timestamp: new Date()
        }
      };
    }

    case actions.DELETE_OBJECTIVE_IN_LIST: {
      const paths = state.pathMap[payload.objective.objective_id];
      const updatedObjectives = deleteObjectiveUsingPaths(
        state.objectives,
        payload.objective,
        paths
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.DELETE_DEPENDENCY_FROM_OBJECTIVES: {
      const updatedObjectives = deleteDependencyObjectivesUsingPaths(
        state.objectives,
        state.pathMap,
        payload.linked_objective_ids,
        payload.dependency_objective_id,
        payload.isDependencyObjectiveDeleted
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.SET_OBJECTIVE_TO_LINK_ACTION: {
      return {
        ...state,
        selectedObjectiveToLink: payload.objective
      };
    }

    case actions.SAVE_LINKED_OBJECTIVES_ACTION: {
      return {
        ...state,
        linkedObjectives: payload.data
      };
    }

    case actions.LINKED_OBJECTIVE_POPUP: {
      return {
        ...state,
        linkedObjectivePopup: payload.objective
      };
    }

    case actions.SET_QUICK_FILTER_ATTRIBUTES: {
      return {
        ...state,
        quickFilterAttributes: payload.data
      };
    }

    case actions.SET_QUICK_FILTERS_SELECTED: {
      return {
        ...state,
        quickFiltersSelected: payload.data
      };
    }

    case appActions.ENABLE_SEARCH_ACTION: {
      return {
        ...state,
        pathMap: null,
        objectives: [],
        flatObjectives: []
      };
    }

    case actions.SAVE_VIEW_DASHBOARD_DATA: {
      return {
        ...state,
        viewDashboardData: payload.data
      };
    }

    case actions.SET_USER_RESTRICTIONS: {
      return {
        ...state,
        currentUserRestrictions: payload.data
      };
    }

    case actions.SAVE_OBJECTIVE_IN_CONTEXT_DATA: {
      const updatedObjectives = updateViewInContext(
        state.objectives,
        payload.objective,
        payload.data,
        payload.isLinkedObjective,
        state.objectiveLinkGettingViewedFor,
        payload.switchObjectiveContextTreeForPrimaryObjectiveIfAvailable
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap,
        alignedObjective: payload.objective
      };
    }

    case actions.CLEAR_ALIGNED_OBJECTIVE: {
      const updatedObjectives = clearAlignedObjective(
        state.objectives,
        payload.objective || state.alignedObjective
      );
      const updatedFlatObjectives = state.flatObjectives;
      const isAlignedIndex = updatedFlatObjectives.findIndex(
        obj => obj.isAligned
      );
      if (isAlignedIndex > -1)
        updatedFlatObjectives[isAlignedIndex].isAligned = false;
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives: updatedFlatObjectives,
        alignedObjective: null,
        linkedObjectivePopup: payload.objective
          ? state.linkedObjectivePopup
          : null,
        objectiveLinkGettingViewedFor: payload.objective
          ? state.objectiveLinkGettingViewedFor
          : null
      };
    }

    case actions.OBJECTIVE_LINK_GETTING_VIEWED_FOR: {
      return {
        ...state,
        objectiveLinkGettingViewedFor: payload.objective
      };
    }

    case actions.UPDATE_VIEW_DASHBOARD_DATA: {
      return {
        ...state,
        updateViewDashboardData: payload.value
      };
    }

    case appActions.RESET_REDUCER:
      return {
        ...state,
        pathMap: null,
        objectives: null,
        selectedObjective: null,
        selectedObjectiveToMove: null,
        searchResultObjectives: null,
        flatObjectives: [],
        paginationData: {
          currentPage: 1,
          showPagination: false
        }
      };

    case DnDActions.REORDER_OBJECTIVES: {
      const updatedObjectives = reorderObjectives(
        state.objectives,
        state.flatObjectives,
        payload.objective_id,
        payload.newIndex,
        payload.oldIndex
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap,
        orderingVersion: state.orderingVersion + 1
      };
    }

    case DnDActions.SET_FLAT_OBJECTIVES:
      return {
        ...state,
        flatObjectives: payload.flatObjectives
      };

    case DnDActions.REORDER_OBJECTIVES_UNDER_SAME_PARENT: {
      const updatedObjectives = reorderObjectivesUnderSameParent(
        state.objectives,
        state.flatObjectives,
        payload.objective_id,
        payload.parent_objective_id,
        payload.destinationIndex,
        payload.sourceIndex,
        payload.positionIndex
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case DnDActions.REORDER_OBJECTIVES_UNDER_DIFF_PARENT: {
      const updatedObjectives = reorderObjectivesUnderDiffParent(
        payload.objective,
        state.objectives,
        state.flatObjectives,
        payload.objective_id,
        payload.parent_objective_id,
        payload.destinationIndex,
        payload.sourceIndex,
        payload.positionIndex
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.SAVE_MOBILE_TRAVERSED_TREE_DATA:
      return {
        ...state,
        mobileTreeTraverseData: payload.data
      };

    case actions.SAVE_TEAM_DETAILS:
      return {
        ...state,
        selectedTeamData: payload.data
      };

    case actions.SAVE_RECENTLY_DROPPED_OBJECTIVE:
      return {
        ...state,
        recentlyDroppedObjective: payload.objective
      };

    case actions.CLEAR_RECENTLY_DROPPED_OBJECTIVE:
      return {
        ...state,
        recentlyDroppedObjective: null
      };

    case DnDActions.REVERT_OBJECTIVE_MOVE_ACTION_REQUEST: {
      const updatedObjectives = dndUndoUtils.revertObjectiveMoveAction(
        state.objectives,
        payload.undoActionData
      );
      const updatedPathMap = createPathMap(updatedObjectives);
      const flatObjectives = flatDeep(updatedObjectives, Infinity);
      return {
        ...state,
        objectives: updatedObjectives,
        flatObjectives,
        pathMap: updatedPathMap
      };
    }

    case actions.SET_SELECTED_ANNUAL_GOAL_FOR_ALIGNMENT:
      return {
        ...state,
        selectedAnnualGoalForAlignment: payload.objective
      };

    case actions.SET_CREATE_OBJECTIVE_SELECTED_CYCLE:
      return {
        ...state,
        createObjectiveSelectedCycle: payload.cycle
      };

    case actions.SET_CREATE_OBJECTIVE_ACTION_STATUS:
      return {
        ...state,
        createObjectiveActionStatus: payload.data
      };

    case actions.SET_ANNUALLY_ALIGNED_OBJECTIVE_DETAILS:
      return {
        ...state,
        annuallyAlignedObjectiveDetails: payload.data
      };

    case actions.SET_ANNUAL_LINKED_OBJECTIVE_POPUP_DATA:
      return {
        ...state,
        annualLinkedObjPopupData: payload.data
      };

    case actions.SET_SELECTED_TEAM_QUICK_FILTERS:
      return {
        ...state,
        selectedTeamQuickFilters: payload.filters
      };

    case actions.UPDATE_PAGE_LOAD_IN_PROGRESS:
      return {
        ...state,
        pageLoadInProgress: payload.status
      };

    case actions.SET_LAST_OBJECTIVE_IN_VIEW_ID:
      return {
        ...state,
        lastObjectiveInViewId: payload.status
      };

    case actions.SET_VIEW_ALIGNMENT_PREVIOUS_VIEW_STATE:
      return {
        ...state,
        viewAlignmentPreviousViewState: payload.state
      };

    default:
      return state;
  }
};
